import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import IntegrationsTab from '@/pages/IntegrationsTab';
import {
  IndianRupee, Shield, Save, CheckCircle, Building2, Eye, EyeOff,
  MapPin, BookOpen, Users, Plus, Trash2, GraduationCap, Mail, Phone, Send,
  Loader2, XCircle, AlertTriangle, AlertCircle, CreditCard, Zap, Trophy, Copy, Share2, ExternalLink,
  Hexagon, Activity, RefreshCw, Sparkles, Webhook, Info, Star
} from 'lucide-react';
import { toast } from 'sonner';
import WebhooksSettings from '@/components/settings/WebhooksSettings';
import AutomationFlagsPanel from '@/components/settings/AutomationFlagsPanel';
import GeofenceLocationCard from '@/components/settings/GeofenceLocationCard';
import SessionReplayCard from '@/components/settings/SessionReplayCard';
// Phase 325b — Per-academy auto-billing settings
import BillingSettingsCard from '@/components/settings/BillingSettingsCard';
// Phase 293 — One-time Razorpay Health Check Wizard
import RazorpayHealthCheckWizard from '@/components/academy/RazorpayHealthCheckWizard';
// Phase 288 — Dynamic WhatsApp Template Routing card (SA only)
import WhatsAppTemplateRoutingCard from '@/components/settings/WhatsAppTemplateRoutingCard';
// Phase 252-Harden — Additive AiSensy WhatsApp gateway config (platform + per-academy modes)
// Phase 291 — AiSensy import removed. The centralised Meta relay
// (services/whatsapp_relay.py) supersedes per-academy WhatsApp providers.
// Phase 266f — single source of truth for the embed snippet (also reused in
// the dashboard Embed Performance tile dialog).
import EmbedTopMomentsBlock from '@/components/EmbedTopMomentsBlock';
// Phase 270c — Resend sender-domain verification pill
import ResendDomainPill from '@/pages/super_admin/ResendDomainPill';

export default function SettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  // === SUPER ADMIN STATE ===
  const [platformSettings, setPlatformSettings] = useState(null);
  const [platformLoading, setPlatformLoading] = useState(true);
  const [platformSaving, setPlatformSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [testWhatsAppNumber, setTestWhatsAppNumber] = useState('');

  const [paymentForm, setPaymentForm] = useState({ razorpay_key_id: '', razorpay_key_secret: '', razorpay_webhook_secret: '', mock_payments: true });
  const [emailForm, setEmailForm] = useState({ resend_api_key: '', resend_from_email: 'contact@out-play.in' });
  const [whatsappForm, setWhatsappForm] = useState({ twilio_account_sid: '', twilio_auth_token: '', twilio_whatsapp_number: '', twilio_sms_number: '' });
  // Phase 68 — GST / Business details for tax-invoice headers
  const [gstForm, setGstForm] = useState({ gst_legal_name: '', gst_gstin: '', gst_address: '', gst_hsn_sac: '998314', gst_pan: '' });
  const [savingGst, setSavingGst] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [testSmsNumber, setTestSmsNumber] = useState('');
  const [testingSms, setTestingSms] = useState(false);
  // Phase 244 — Telephony pre-flight diagnostics state
  const [diagRunning, setDiagRunning] = useState(false);
  const [diag, setDiag] = useState(null);
  // Phase 244.b — Smart Sender Picker — auto-discover provisioned senders so
  // the operator can pick from a dropdown instead of typing E.164 by hand.
  const [senderDiscovery, setSenderDiscovery] = useState({
    whatsapp_senders: [], sms_senders: [], sandbox: null, creds_ok: false, message: '',
  });
  const [discoveryLoading, setDiscoveryLoading] = useState(false);

  const fetchTwilioSenders = useCallback(async () => {
    setDiscoveryLoading(true);
    try {
      const r = await api.get('/super-admin/telephony/senders');
      setSenderDiscovery(r.data);
    } catch {
      // Swallow — falls back to plain text input
    } finally {
      setDiscoveryLoading(false);
    }
  }, []);

  // === Phase 89B.2 — Meta Marketing API (Super Admin only) ===
  const [metaStatus, setMetaStatus] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaHealth, setMetaHealth] = useState(null);
  const [metaHealthChecking, setMetaHealthChecking] = useState(false);
  const [showMetaAppSecret, setShowMetaAppSecret] = useState(false);
  const [showMetaSystemToken, setShowMetaSystemToken] = useState(false);
  const [showMetaPageToken, setShowMetaPageToken] = useState(false);
  // Phase 266k — Meta WhatsApp Cloud credentials (separate from SocialBee Marketing token)
  const [showMetaWhatsAppToken, setShowMetaWhatsAppToken] = useState(false);
  // Phase 266l — "Send test WhatsApp" button state
  const [metaWhatsAppTestForm, setMetaWhatsAppTestForm] = useState({ to_number: '', template_name: '', language_code: 'en_US' });
  const [sendingMetaWhatsAppTest, setSendingMetaWhatsAppTest] = useState(false);
  const [metaWhatsAppTestResult, setMetaWhatsAppTestResult] = useState(null);
  const [metaForm, setMetaForm] = useState({
    meta_app_id: '',
    meta_business_portfolio_id: '',
    meta_app_secret: '',
    meta_system_user_token: '',
    meta_default_ad_account_id: '',
    meta_default_page_id: '',
    meta_default_page_access_token: '',
    meta_default_ig_business_account_id: '',
    // Phase 266k — WhatsApp Cloud platform fields
    meta_whatsapp_token: '',
    meta_whatsapp_phone_number_id: '',
    meta_waba_id: '',
  });

  // === ACADEMY OWNER STATE ===
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecretAcademy, setShowSecretAcademy] = useState(false);
  // Phase 90.2 — Test Razorpay connection (academy owner)
  const [rzpTestRunning, setRzpTestRunning] = useState(false);
  const [rzpTestResult, setRzpTestResult] = useState(null);
  const [form, setForm] = useState({ razorpay_key_id: '', razorpay_key_secret: '', mock_payments: true, name: '', city: '', phone: '' });
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [centers, setCenters] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  // Phase 96 — Departments collection (HRMS dropdown source)
  const [departments, setDepartments] = useState([]);
  const [showAddCenter, setShowAddCenter] = useState(false);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [showAddDept, setShowAddDept] = useState(false);
  const [centerForm, setCenterForm] = useState({ name: '', address: '', capacity: '' });
  const [courseForm, setCourseForm] = useState({ name: '', description: '', duration: '', fees: '' });
  const [batchForm, setBatchForm] = useState({ name: '', timing: '', max_students: '' });
  const [deptForm, setDeptForm] = useState({ name: '', description: '' });

  // === SUPER ADMIN FETCH ===
  const fetchPlatformSettings = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/super-admin/settings');
      setPlatformSettings(res.data);
      setPaymentForm({
        razorpay_key_id: res.data.razorpay_key_id || '',
        razorpay_key_secret: '',
        razorpay_webhook_secret: '',
        mock_payments: res.data.mock_payments ?? true
      });
      setEmailForm({
        resend_api_key: res.data.resend_api_key || '',
        resend_from_email: res.data.resend_from_email || 'contact@out-play.in'
      });
      setWhatsappForm({
        twilio_account_sid: res.data.twilio_account_sid || '',
        twilio_auth_token: '',
        twilio_whatsapp_number: res.data.twilio_whatsapp_number || '',
        twilio_sms_number: res.data.twilio_sms_number || ''
      });
      // Phase 68 — GST hydration
      setGstForm({
        gst_legal_name: res.data.gst_legal_name || res.data.company_name || '',
        gst_gstin: res.data.gst_gstin || res.data.gstin || '',
        gst_address: res.data.gst_address || res.data.company_address || '',
        gst_hsn_sac: res.data.gst_hsn_sac || '998314',
        gst_pan: res.data.gst_pan || ''
      });
    } catch (err) { console.error(err); }
    finally { setPlatformLoading(false); }
  }, [isSuperAdmin]);

  // === ACADEMY FETCH ===
  const fetchSettings = useCallback(async () => {
    if (isSuperAdmin) { setLoading(false); return; }
    try {
      const res = await api.get('/settings');
      setSettings(res.data);
      setForm({
        razorpay_key_id: res.data.razorpay_key_id || '', razorpay_key_secret: '',
        name: res.data.academy_name || '', city: res.data.city || '', phone: res.data.phone || '',
        resend_api_key: res.data.resend_api_key || '', resend_from_email: res.data.resend_from_email || '',
        twilio_account_sid: res.data.twilio_account_sid || '', twilio_auth_token: '',
        twilio_whatsapp_number: res.data.twilio_whatsapp_number || '',
        auto_seed_avatars: res.data.auto_seed_avatars !== false,
        // Phase 206 — Per-academy hard cap for the AI Discount Co-Pilot.
        max_scholarship_pct: res.data.max_scholarship_pct ?? 25,
        // Phase 297 — Google Reviews URL for the NPS reputation engine.
        google_review_url: res.data.google_review_url || '',
        // Phase 310.5 — Subscription price (₹) feeds the ROI multiplier.
        subscription_price_inr: res.data.subscription_price_inr || ''
      });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [isSuperAdmin]);

  const fetchConfig = useCallback(async () => {
    if (isSuperAdmin) return;
    try {
      const [cRes, coRes, bRes, dRes] = await Promise.all([
        api.get('/academy/centers').catch(() => ({ data: [] })),
        api.get('/academy/courses').catch(() => ({ data: [] })),
        api.get('/academy/batches').catch(() => ({ data: [] })),
        api.get('/academy/departments').catch(() => ({ data: [] }))
      ]);
      setCenters(cRes.data || []); setCourses(coRes.data || []); setBatches(bRes.data || []); setDepartments(dRes.data || []);
    } catch (err) { console.error(err); }
  }, [isSuperAdmin]);

  // === Phase 89B.2 — Meta Marketing API: fetchMetaStatus (declared BEFORE useEffect to avoid TDZ) ===
  // Returns the fresh status so callers (e.g., Save & Verify) can decide whether to chain a health check.
  const formatRelativeTime = (isoString) => {
    if (!isoString) return null;
    const now = Date.now();
    const then = new Date(isoString).getTime();
    const diffSec = Math.max(0, Math.floor((now - then) / 1000));
    if (diffSec < 5) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
    return new Date(isoString).toLocaleDateString();
  };

  const fetchMetaStatus = useCallback(async () => {
    if (!isSuperAdmin) return null;
    setMetaLoading(true);
    try {
      const res = await api.get('/super-admin/socialbee/meta-status');
      setMetaStatus(res.data);
      setMetaForm({
        meta_app_id: res.data.meta_app_id || '',
        meta_business_portfolio_id: res.data.meta_business_portfolio_id || '',
        meta_app_secret: '',
        meta_system_user_token: '',
        meta_default_ad_account_id: res.data.meta_default_ad_account_id || '',
        meta_default_page_id: res.data.meta_default_page_id || '',
        meta_default_page_access_token: '',
        meta_default_ig_business_account_id: res.data.meta_default_ig_business_account_id || '',
        // Phase 266k — hydrate plaintext IDs; token field stays empty (server returns redacted preview).
        meta_whatsapp_token: '',
        meta_whatsapp_phone_number_id: res.data.meta_whatsapp_phone_number_id || '',
        meta_waba_id: res.data.meta_waba_id || '',
      });
      return res.data;
    } catch (err) {
      console.error('Meta status fetch failed:', err);
      return null;
    } finally { setMetaLoading(false); }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) { fetchPlatformSettings(); fetchMetaStatus(); fetchTwilioSenders(); }
    else { fetchSettings(); fetchConfig(); api.get('/academy/logo').then(r => setLogoUrl(r.data?.logo_url)).catch(() => {}); }
  }, [isSuperAdmin, fetchPlatformSettings, fetchSettings, fetchConfig, fetchMetaStatus]);

  // === Phase 89B.2 — Meta Marketing API handlers ===
  // Runs the live health check against Meta Graph API. Accepts an optional
  // `suppressToast` flag so the Save & Verify chain doesn't double-toast.
  const runMetaHealthCheck = async (suppressToast = false) => {
    setMetaHealthChecking(true);
    setMetaHealth(null);
    try {
      const res = await api.post('/super-admin/socialbee/meta-health-check');
      setMetaHealth(res.data);
      if (!suppressToast) {
        if (res.data?.ok) toast.success(`Healthy · ${res.data.latency_ms ?? '?'} ms`);
        else toast.error(res.data?.reason || res.data?.error || 'Health check failed');
      }
      return res.data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Health check failed';
      const errPayload = { ok: false, error: msg };
      setMetaHealth(errPayload);
      if (!suppressToast) toast.error(msg);
      return errPayload;
    } finally { setMetaHealthChecking(false); }
  };

  const handleSaveMeta = async () => {
    setMetaSaving(true);
    try {
      const payload = {};
      ['meta_app_id', 'meta_business_portfolio_id', 'meta_default_ad_account_id', 'meta_default_page_id', 'meta_default_ig_business_account_id', 'meta_whatsapp_phone_number_id', 'meta_waba_id'].forEach((k) => {
        if (metaForm[k] !== undefined && metaForm[k] !== null) payload[k] = metaForm[k].trim();
      });
      ['meta_app_secret', 'meta_system_user_token', 'meta_default_page_access_token', 'meta_whatsapp_token'].forEach((k) => {
        if (metaForm[k] && metaForm[k].trim()) payload[k] = metaForm[k].trim();
      });
      if (Object.keys(payload).length === 0) { toast.error('Nothing to save'); setMetaSaving(false); return; }
      await api.post('/super-admin/socialbee/meta-secrets', payload);
      // Wipe local secret state so they aren't re-sent on next save.
      setMetaForm((f) => ({ ...f, meta_app_secret: '', meta_system_user_token: '', meta_default_page_access_token: '', meta_whatsapp_token: '' }));

      // Save & Verify — auto-chain a live health check when creds are sufficient.
      const fresh = await fetchMetaStatus();
      if (fresh?.ready_for_live) {
        const health = await runMetaHealthCheck(true);
        if (health?.ok) {
          toast.success(`Saved & verified · ${health.latency_ms ?? '?'} ms`);
        } else {
          toast.error(`Saved · health check failed (${health?.status ? `HTTP ${health.status}` : (health?.reason || 'see panel')})`);
        }
      } else {
        toast.success('Meta credentials saved. Add App ID + System User Token to enable live publishing.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Meta credentials');
    } finally { setMetaSaving(false); }
  };

  const handleMetaHealthCheck = () => runMetaHealthCheck(false);

  // Phase 266l — "Send test WhatsApp" button handler.
  // Hits the dedicated test endpoint which REFUSES with HTTP 412 when creds
  // aren't saved (so the SA sees "Save credentials first" instead of a
  // misleading mock-mode success).
  const handleSendMetaWhatsAppTest = async () => {
    const to = (metaWhatsAppTestForm.to_number || '').trim();
    const tpl = (metaWhatsAppTestForm.template_name || '').trim();
    if (!to) { toast.error('Enter a recipient phone number'); return; }
    if (!tpl) { toast.error('Enter a Meta-approved template name'); return; }
    setSendingMetaWhatsAppTest(true);
    setMetaWhatsAppTestResult(null);
    try {
      const res = await api.post('/super-admin/socialbee/meta-whatsapp-test', {
        to_number: to,
        template_name: tpl,
        language_code: (metaWhatsAppTestForm.language_code || 'en_US').trim() || 'en_US',
      });
      setMetaWhatsAppTestResult({ http_success: true, payload: res.data });
      if (res.data?.ok) {
        toast.success(`WhatsApp test sent · message_id: ${res.data.message_id || 'n/a'}`);
      } else {
        toast.error(`Meta rejected the send: ${res.data?.error || 'see panel'}`);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Test send failed';
      setMetaWhatsAppTestResult({ http_success: false, status, detail });
      if (status === 412) {
        toast.error('Save credentials first');
      } else {
        toast.error(detail);
      }
    } finally { setSendingMetaWhatsAppTest(false); }
  };

  // === SUPER ADMIN SAVE HANDLERS ===
  const handleSavePayment = async () => {
    setPlatformSaving(true);
    try {
      const payload = { ...paymentForm, mock_payments: false };
      if (!payload.razorpay_key_secret) delete payload.razorpay_key_secret;
      if (!payload.razorpay_webhook_secret) delete payload.razorpay_webhook_secret;
      await api.put('/super-admin/settings', payload);
      toast.success('Payment settings saved');
      fetchPlatformSettings();
    } catch (err) { toast.error('Failed to save'); }
    finally { setPlatformSaving(false); }
  };

  // Phase 68 — Save GST / Business details for tax-invoice headers
  const handleSaveGst = async () => {
    setSavingGst(true);
    try {
      await api.put('/super-admin/settings', gstForm);
      toast.success('GST & business details saved — will appear on every Tax Invoice and Receipt');
      fetchPlatformSettings();
    } catch (err) {
      toast.error('Failed to save GST details');
    } finally { setSavingGst(false); }
  };

  const handleSaveEmail = async () => {
    setPlatformSaving(true);
    try {
      await api.put('/super-admin/settings', emailForm);
      toast.success('Email settings saved');
      fetchPlatformSettings();
    } catch (err) { toast.error('Failed to save'); }
    finally { setPlatformSaving(false); }
  };

  const handleSaveWhatsApp = async () => {
    setPlatformSaving(true);
    try {
      const payload = { ...whatsappForm };
      if (!payload.twilio_auth_token) delete payload.twilio_auth_token;
      await api.put('/super-admin/settings', payload);
      toast.success('WhatsApp settings saved');
      fetchPlatformSettings();
    } catch (err) { toast.error('Failed to save'); }
    finally { setPlatformSaving(false); }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      // Save first, then test
      if (emailForm.resend_api_key) await api.put('/super-admin/settings', emailForm);
      const res = await api.post('/super-admin/settings/test-email', { to_email: user?.email || '' });
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) { toast.error('Test failed'); }
    finally { setTestingEmail(false); }
  };

  const handleTestWhatsApp = async () => {
    if (!testWhatsAppNumber) { toast.error('Enter a phone number to test'); return; }
    setTestingWhatsApp(true);
    try {
      // Save first, then test
      const payload = { ...whatsappForm };
      if (!payload.twilio_auth_token) delete payload.twilio_auth_token;
      await api.put('/super-admin/settings', payload);
      const res = await api.post('/super-admin/settings/test-whatsapp', { to_number: testWhatsAppNumber });
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) { toast.error('Test failed'); }
    finally { setTestingWhatsApp(false); }
  };

  const handleTestSms = async () => {
    if (!testSmsNumber) { toast.error('Enter a phone number to test'); return; }
    setTestingSms(true);
    try {
      const payload = { ...whatsappForm };
      if (!payload.twilio_auth_token) delete payload.twilio_auth_token;
      await api.put('/super-admin/settings', payload);
      const res = await api.post('/super-admin/settings/test-sms', { to_number: testSmsNumber });
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) { toast.error('Test failed'); }
    finally { setTestingSms(false); }
  };

  // Phase 244 — Pre-flight diagnostics: validates creds + sender provisioning
  // WITHOUT actually firing a real message. Saves current form first so the
  // diagnostic sees the latest values.
  const runTelephonyPreflight = async () => {
    setDiagRunning(true);
    setDiag(null);
    try {
      const payload = { ...whatsappForm };
      if (!payload.twilio_auth_token) delete payload.twilio_auth_token;
      await api.put('/super-admin/settings', payload);
      const r = await api.post('/super-admin/telephony/verify-sender');
      setDiag(r.data);
      // Phase 244.b — refresh sender dropdown lists now that we've validated creds
      fetchTwilioSenders();
      const wa = r.data?.whatsapp?.status;
      const sms = r.data?.sms?.status;
      const credsOk = r.data?.creds?.status === 'valid';
      if (credsOk && (wa === 'verified' || wa === 'not_configured') && (sms === 'verified' || sms === 'not_configured')) {
        toast.success('Twilio configuration looks healthy');
      } else if (!credsOk) {
        toast.error('Twilio credentials failed validation');
      } else {
        toast.warning('Diagnostic complete — see issues below');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setDiagRunning(false);
    }
  };

  // === ACADEMY SAVE ===
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.razorpay_key_secret) delete payload.razorpay_key_secret;
      // Phase 310.5 — Coerce subscription_price_inr to a number (or omit).
      // The backend expects an integer in [0..1_000_000]; an empty string
      // would be 400'd, so drop it when blank.
      if (payload.subscription_price_inr === '' || payload.subscription_price_inr === null) {
        delete payload.subscription_price_inr;
      } else {
        payload.subscription_price_inr = parseInt(payload.subscription_price_inr, 10) || 0;
      }
      await api.put('/settings', payload);
      toast.success('Settings saved');
      fetchSettings();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save';
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  // Phase 90.2 — One-click Razorpay credential probe for academy owners.
  const handleTestRazorpayConnection = async () => {
    setRzpTestRunning(true);
    setRzpTestResult(null);
    try {
      const r = await api.post('/academy/razorpay/test-connection');
      setRzpTestResult(r.data);
      if (r.data?.ok) toast.success(`Razorpay healthy · ${r.data.latency_ms ?? '?'} ms`);
      else toast.error(r.data?.reason || 'Razorpay rejected the credentials');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Test connection failed';
      setRzpTestResult({ ok: false, error: msg, reason: msg });
      toast.error(msg);
    } finally { setRzpTestRunning(false); }
  };

  const handleAddCenter = async (e) => { e.preventDefault(); try { await api.post('/academy/centers', { ...centerForm, capacity: parseInt(centerForm.capacity) || 0 }); toast.success('Center added'); setShowAddCenter(false); setCenterForm({ name: '', address: '', capacity: '' }); fetchConfig(); } catch (err) { apiErrorToast(err, 'Failed to add center'); } };
  const handleAddCourse = async (e) => { e.preventDefault(); try { await api.post('/academy/courses', { ...courseForm, fees: parseFloat(courseForm.fees) || 0 }); toast.success('Course added'); setShowAddCourse(false); setCourseForm({ name: '', description: '', duration: '', fees: '' }); fetchConfig(); } catch (err) { apiErrorToast(err, 'Failed to add course'); } };
  const handleAddBatch = async (e) => { e.preventDefault(); try { await api.post('/academy/batches', { ...batchForm, max_students: parseInt(batchForm.max_students) || 30 }); toast.success('Batch added'); setShowAddBatch(false); setBatchForm({ name: '', timing: '', max_students: '' }); fetchConfig(); } catch (err) { apiErrorToast(err, 'Failed to add batch'); } };
  const handleAddDept = async (e) => { e.preventDefault(); try { await api.post('/academy/departments', deptForm); toast.success('Department added'); setShowAddDept(false); setDeptForm({ name: '', description: '' }); fetchConfig(); } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); } };

  // Loading
  if (isSuperAdmin && platformLoading) return (<div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-48 rounded-xl" /></div>);
  if (!isSuperAdmin && loading) return (<div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-48 rounded-xl" /></div>);

  // ============================================================
  // SUPER ADMIN VIEW - Only Payments, Email, WhatsApp
  // ============================================================
  if (isSuperAdmin) {
    return (
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-2xl font-display font-semibold">Platform Settings</h1>
          <p className="text-sm text-muted-foreground">Configure payment gateway, email & WhatsApp integrations</p>
        </div>

        <Tabs defaultValue="payments">
          <TabsList>
            <TabsTrigger value="payments" data-testid="tab-payments"><IndianRupee className="h-3.5 w-3.5 mr-1" /> Payments</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email"><Mail className="h-3.5 w-3.5 mr-1" /> Email</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><Phone className="h-3.5 w-3.5 mr-1" /> WhatsApp</TabsTrigger>
            <TabsTrigger value="gst" data-testid="tab-gst"><Building2 className="h-3.5 w-3.5 mr-1" /> GST & Business</TabsTrigger>
            <TabsTrigger value="meta" data-testid="tab-meta"><Hexagon className="h-3.5 w-3.5 mr-1" /> Meta Ads</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing"><Sparkles className="h-3.5 w-3.5 mr-1" /> Auto-Bill</TabsTrigger>
          </TabsList>

          {/* Payments Tab */}
          <TabsContent value="payments">
            {/* Setup Guide Card */}
            <Card className="max-w-2xl mb-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 border-emerald-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-emerald-600" />
                  How to Enable Online Fee Payments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                    <div className="flex-1">
                      <p className="font-medium text-emerald-900 dark:text-emerald-100">Create Razorpay Account</p>
                      <p className="text-emerald-700 dark:text-emerald-300 text-xs mt-0.5">
                        Visit <a href="https://dashboard.razorpay.com/signup" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-600">dashboard.razorpay.com/signup</a> • Complete KYC (24-48hrs approval)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                    <div className="flex-1">
                      <p className="font-medium text-emerald-900 dark:text-emerald-100">Get API Keys</p>
                      <p className="text-emerald-700 dark:text-emerald-300 text-xs mt-0.5">
                        <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-600">Dashboard</a> → Settings → API Keys → Generate Test/Live Keys
                      </p>
                      <div className="mt-2 p-2 bg-white/60 dark:bg-slate-900/60 rounded text-xs font-mono">
                        <p className="text-emerald-600">Key ID: rzp_test_xxxxx...</p>
                        <p className="text-rose-600 mt-0.5">Secret: xxxxxxxxxxxxx</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                    <div className="flex-1">
                      <p className="font-medium text-emerald-900 dark:text-emerald-100">Paste Keys Below & Save</p>
                      <p className="text-emerald-700 dark:text-emerald-300 text-xs mt-0.5">
                        Students can pay instantly via UPI, Cards, Net Banking • Money reaches your bank in 2 days
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                      <strong>Security:</strong> Never share your Key Secret publicly. Use Test keys for testing, Live keys for production.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle className="text-base">Payment Settings (Razorpay)</CardTitle>
                <CardDescription>Configure for fee collection and event payments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Razorpay Key ID</Label>
                  <Input value={paymentForm.razorpay_key_id} onChange={(e) => setPaymentForm({...paymentForm, razorpay_key_id: e.target.value})} placeholder="rzp_test_xxxxxxxxxxxxx" data-testid="razorpay-key-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Razorpay Key Secret</Label>
                  <div className="relative">
                    <Input type={showSecret ? 'text' : 'password'} value={paymentForm.razorpay_key_secret} onChange={(e) => setPaymentForm({...paymentForm, razorpay_key_secret: e.target.value})} placeholder="Leave empty to keep existing" data-testid="razorpay-secret-input" />
                    <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150">
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {/* Phase 68 — Razorpay Webhook Secret (for verifying payment.captured events server-side) */}
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    Razorpay Webhook Secret
                    {platformSettings?.razorpay_webhook_configured && <CheckCircle className="h-3 w-3 text-emerald-600" />}
                  </Label>
                  <div className="relative">
                    <Input type={showWebhookSecret ? 'text' : 'password'} value={paymentForm.razorpay_webhook_secret} onChange={(e) => setPaymentForm({...paymentForm, razorpay_webhook_secret: e.target.value})} placeholder={platformSettings?.razorpay_webhook_configured ? 'Configured — leave empty to keep existing' : 'Set in Razorpay Dashboard → Webhooks'} data-testid="razorpay-webhook-secret-input" />
                    <button type="button" onClick={() => setShowWebhookSecret(!showWebhookSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150">
                      {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Required to verify the <code className="bg-muted px-1 rounded">payment.captured</code> webhook from Razorpay. Without this set, the webhook accepts unsigned events (DEV only).</p>
                </div>
                {platformSettings?.razorpay_configured && (
                  <div className="flex items-center gap-2 text-sm"><CheckCircle className="h-4 w-4 text-emerald-600" /><span className="text-emerald-600 font-medium">Razorpay configured</span></div>
                )}
                <Button onClick={handleSavePayment} disabled={platformSaving} className="w-full" data-testid="save-payment-btn">
                  <Save className="h-4 w-4 mr-2" /> {platformSaving ? 'Saving...' : 'Save Payment Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email">
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Email Configuration (Resend)</CardTitle>
                <CardDescription>Send invoices, reminders & notifications via email</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Resend API Key</Label>
                  <Input value={emailForm.resend_api_key} onChange={(e) => setEmailForm({...emailForm, resend_api_key: e.target.value})} placeholder="re_xxxxxxxxxxxxx" data-testid="resend-key-input" />
                  <p className="text-xs text-muted-foreground">Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-primary hover:underline">resend.com/api-keys</a></p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">From Email</Label>
                  <Input value={emailForm.resend_from_email} onChange={(e) => setEmailForm({...emailForm, resend_from_email: e.target.value})} placeholder="contact@youracademy.com" data-testid="resend-from-input" />
                </div>
                {/* Phase 270c — live Resend sender-domain verification status (always shown in Email tab) */}
                <div className="rounded-md border border-border bg-muted/20 p-2.5" data-testid="settings-resend-domain-gate">
                  <ResendDomainPill testIdPrefix="settings-resend-domain" />
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    Domain must show <strong>Verified</strong> before emails will deliver. Complete DKIM/SPF setup at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-primary hover:underline">resend.com/domains</a>.
                  </p>
                </div>
                {platformSettings?.resend_configured && (
                  <div className="flex items-center gap-2 text-sm"><CheckCircle className="h-4 w-4 text-emerald-600" /><span className="text-emerald-600 font-medium">Resend configured</span></div>
                )}
                <Separator />
                <div className="space-y-2">
                  <p className="text-sm font-medium">Email Templates</p>
                  <div className="grid grid-cols-3 gap-2">
                    {['Fee Reminder', 'Welcome Email', 'Progress Report'].map(t => (
                      <div key={t} className="p-3 rounded-lg border text-center">
                        <p className="text-xs font-medium">{t}</p>
                        <Badge variant="secondary" className="text-[10px] mt-1">Active</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveEmail} disabled={platformSaving} className="flex-1" data-testid="save-email-btn">
                    <Save className="h-4 w-4 mr-2" /> {platformSaving ? 'Saving...' : 'Save Email Settings'}
                  </Button>
                  <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail} data-testid="test-email-btn">
                    {testingEmail ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Test
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WhatsApp / SMS Tab — Phase 66.1: now powers the Out-Play Relay */}
          <TabsContent value="whatsapp">
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Phone className="h-5 w-5 text-primary" /> WhatsApp + SMS Relay (Twilio)</CardTitle>
                <CardDescription>Powers the Out-Play Fail-Safe Relay — academies without their own Twilio land here automatically</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Phase 66.1 — Live relay impact stat */}
                {platformSettings?.relay_stats && (
                  <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 p-3" data-testid="relay-stats-card">
                    <p className="text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-300 font-semibold mb-2">Relay Impact</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-xl font-display font-bold text-blue-700 dark:text-blue-300 tabular-nums">{platformSettings.relay_stats.total_academies}</p>
                        <p className="text-[10px] text-muted-foreground">Academies</p>
                      </div>
                      <div>
                        <p className="text-xl font-display font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{platformSettings.relay_stats.own_keys}</p>
                        <p className="text-[10px] text-muted-foreground">Self-hosted</p>
                      </div>
                      <div>
                        <p className="text-xl font-display font-bold text-amber-700 dark:text-amber-300 tabular-nums">{platformSettings.relay_stats.relay_dependent}</p>
                        <p className="text-[10px] text-muted-foreground">On Relay</p>
                      </div>
                    </div>
                    {platformSettings.relay_stats.relay_dependent > 0 && !platformSettings.twilio_configured && (
                      <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-2 leading-relaxed">
                        ⚠️ <strong>{platformSettings.relay_stats.relay_dependent}</strong> academy(ies) have no Twilio of their own. Configure platform credentials below to deliver their WhatsApp/SMS.
                      </p>
                    )}
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">Twilio Account SID</Label>
                  <Input value={whatsappForm.twilio_account_sid} onChange={(e) => setWhatsappForm({...whatsappForm, twilio_account_sid: e.target.value})} placeholder="ACxxxxxxxxxxxxxxxxx" data-testid="twilio-sid-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Twilio Auth Token</Label>
                  <div className="relative">
                    <Input type={showTwilioToken ? 'text' : 'password'} value={whatsappForm.twilio_auth_token} onChange={(e) => setWhatsappForm({...whatsappForm, twilio_auth_token: e.target.value})} placeholder="Leave empty to keep existing" data-testid="twilio-token-input" />
                    <button type="button" onClick={() => setShowTwilioToken(!showTwilioToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150">
                      {showTwilioToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {platformSettings?.twilio_auth_token_set && <p className="text-xs text-emerald-600">Auth token is set</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1">WhatsApp Number {platformSettings?.twilio_whatsapp_ready && <CheckCircle className="h-3 w-3 text-emerald-600" />}</Label>
                    {(() => {
                      // Phase 244.b — Smart Sender Picker: dropdown when discovery
                      // has results, plain text input otherwise. Currently saved
                      // value is preserved as a "custom" option to prevent silent
                      // data loss if the API hiccups or the number was set offline.
                      const options = senderDiscovery.whatsapp_senders || [];
                      const sandbox = senderDiscovery.sandbox;
                      const current = whatsappForm.twilio_whatsapp_number;
                      const allKnown = [
                        ...options,
                        ...(sandbox ? [sandbox] : []),
                      ];
                      const currentInList = !current || allKnown.some(o => o.phone_number === current);
                      const showPicker = options.length > 0 || sandbox;
                      if (!showPicker) {
                        return (
                          <Input value={current} onChange={(e) => setWhatsappForm({...whatsappForm, twilio_whatsapp_number: e.target.value})} placeholder="+14155238886" data-testid="twilio-number-input" />
                        );
                      }
                      return (
                        <select
                          value={current}
                          onChange={(e) => setWhatsappForm({...whatsappForm, twilio_whatsapp_number: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background"
                          data-testid="twilio-number-select"
                        >
                          <option value="">— Pick a verified sender —</option>
                          {options.map((o, i) => (
                            <option key={`wa-${i}`} value={o.phone_number}>🟢 {o.display_name !== o.phone_number ? `${o.display_name} (${o.phone_number})` : o.phone_number}</option>
                          ))}
                          {sandbox && (
                            <option value={sandbox.phone_number}>🧪 {sandbox.display_name} ({sandbox.phone_number})</option>
                          )}
                          {current && !currentInList && (
                            <option value={current}>⚠ {current} (saved, not in Twilio account)</option>
                          )}
                        </select>
                      );
                    })()}
                    <p className="text-[10px] text-muted-foreground">Twilio Sandbox or approved sender</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1">SMS Number {platformSettings?.twilio_sms_ready && <CheckCircle className="h-3 w-3 text-emerald-600" />}</Label>
                    {(() => {
                      const options = senderDiscovery.sms_senders || [];
                      const current = whatsappForm.twilio_sms_number;
                      const currentInList = !current || options.some(o => o.phone_number === current);
                      if (options.length === 0) {
                        return (
                          <Input value={current} onChange={(e) => setWhatsappForm({...whatsappForm, twilio_sms_number: e.target.value})} placeholder="+12025551234" data-testid="twilio-sms-number-input" />
                        );
                      }
                      return (
                        <select
                          value={current}
                          onChange={(e) => setWhatsappForm({...whatsappForm, twilio_sms_number: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background"
                          data-testid="twilio-sms-number-select"
                        >
                          <option value="">— Pick a verified sender —</option>
                          {options.map((o, i) => (
                            <option key={`sms-${i}`} value={o.phone_number}>📱 {o.display_name !== o.phone_number ? `${o.display_name} (${o.phone_number})` : o.phone_number}</option>
                          ))}
                          {current && !currentInList && (
                            <option value={current}>⚠ {current} (saved, not in Twilio account)</option>
                          )}
                        </select>
                      );
                    })()}
                    <p className="text-[10px] text-muted-foreground">A2P 10DLC / India sender. Falls back to WhatsApp number if blank.</p>
                  </div>
                </div>
                {/* Phase 244.b — Sync senders control row */}
                <div className="flex items-center justify-between text-[11px] text-muted-foreground -mt-1">
                  <span>
                    {senderDiscovery.creds_ok
                      ? `${senderDiscovery.whatsapp_senders.length} WhatsApp · ${senderDiscovery.sms_senders.length} SMS verified senders discovered`
                      : 'Save creds + click Sync to auto-populate verified senders'}
                  </span>
                  <button
                    type="button"
                    onClick={fetchTwilioSenders}
                    disabled={discoveryLoading}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-input hover:bg-accent text-foreground font-semibold disabled:opacity-50"
                    data-testid="twilio-sync-senders-btn"
                  >
                    <RefreshCw className={`h-3 w-3 ${discoveryLoading ? 'animate-spin' : ''}`} /> Sync Senders
                  </button>
                </div>
                {platformSettings?.twilio_configured && (
                  <div className="flex items-center gap-2 text-sm"><CheckCircle className="h-4 w-4 text-emerald-600" /><span className="text-emerald-600 font-medium">Twilio configured — relay is LIVE</span></div>
                )}
                <Separator />
                <div className="space-y-3">
                  <p className="text-sm font-medium">Test Delivery</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Test WhatsApp to</Label>
                      <Input value={testWhatsAppNumber} onChange={(e) => setTestWhatsAppNumber(e.target.value)} placeholder="+919876543210" data-testid="test-whatsapp-number" />
                      <Button variant="outline" size="sm" className="w-full" onClick={handleTestWhatsApp} disabled={testingWhatsApp} data-testid="test-whatsapp-btn">
                        {testingWhatsApp ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Send Test WhatsApp
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Test SMS to</Label>
                      <Input value={testSmsNumber} onChange={(e) => setTestSmsNumber(e.target.value)} placeholder="+919876543210" data-testid="test-sms-number" />
                      <Button variant="outline" size="sm" className="w-full" onClick={handleTestSms} disabled={testingSms} data-testid="test-sms-btn">
                        {testingSms ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Send Test SMS
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Phase 244 — Pre-flight Diagnostics */}
                <Separator />
                <div className="space-y-2.5" data-testid="telephony-preflight-block">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <p className="text-sm font-medium">Pre-flight Diagnostics</p>
                      <p className="text-[11px] text-muted-foreground leading-snug max-w-md mt-0.5">
                        Verifies your creds and that the saved WhatsApp / SMS numbers are actually provisioned on Twilio — without sending a real message or consuming quota. Use this to debug Twilio error <span className="font-mono">63007</span>.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={runTelephonyPreflight}
                      disabled={diagRunning}
                      data-testid="telephony-preflight-btn"
                    >
                      {diagRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Activity className="h-3.5 w-3.5 mr-1" />}
                      Test Sender Validity
                    </Button>
                  </div>

                  {diag && (
                    <div className="space-y-2" data-testid="telephony-preflight-results">
                      <div
                        data-testid="preflight-row-creds"
                        className={`rounded-lg border p-3 text-[11px] flex items-start gap-2 ${
                          diag.creds.status === 'valid'
                            ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200'
                            : 'border-rose-200 bg-rose-50 dark:bg-rose-950/20 text-rose-800 dark:text-rose-200'
                        }`}
                      >
                        {diag.creds.status === 'valid' ? <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                        <div className="flex-1">
                          <p className="font-bold mb-0.5">Credentials</p>
                          <p className="leading-snug">{diag.creds.message}</p>
                        </div>
                      </div>

                      {['verified', 'sandbox_active', 'unprovisioned', 'not_configured'].includes(diag.whatsapp.status) && (
                        <div
                          data-testid="preflight-row-whatsapp"
                          className={`rounded-lg border p-3 text-[11px] flex items-start gap-2 ${
                            diag.whatsapp.status === 'verified'
                              ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200'
                              : diag.whatsapp.status === 'sandbox_active'
                              ? 'border-amber-200 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200'
                              : diag.whatsapp.status === 'unprovisioned'
                              ? 'border-rose-200 bg-rose-50 dark:bg-rose-950/20 text-rose-800 dark:text-rose-200'
                              : 'border-slate-200 bg-slate-50 dark:bg-slate-900/40 text-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {diag.whatsapp.status === 'verified' ? <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                          <div className="flex-1">
                            <p className="font-bold mb-0.5">
                              WhatsApp Sender {diag.whatsapp.number && <span className="font-mono opacity-70">({diag.whatsapp.number})</span>}
                              {diag.whatsapp.error_code && <span className="ml-1.5 font-mono text-[9px] uppercase opacity-70">code {diag.whatsapp.error_code}</span>}
                            </p>
                            <p className="leading-snug">{diag.whatsapp.message}</p>
                            {diag.whatsapp.remediation && (
                              <p className="leading-snug mt-1 pt-1 border-t border-current/10 opacity-90">
                                <strong>Fix:</strong> {diag.whatsapp.remediation}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {['verified', 'unprovisioned', 'not_configured'].includes(diag.sms.status) && (
                        <div
                          data-testid="preflight-row-sms"
                          className={`rounded-lg border p-3 text-[11px] flex items-start gap-2 ${
                            diag.sms.status === 'verified'
                              ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200'
                              : diag.sms.status === 'unprovisioned'
                              ? 'border-rose-200 bg-rose-50 dark:bg-rose-950/20 text-rose-800 dark:text-rose-200'
                              : 'border-slate-200 bg-slate-50 dark:bg-slate-900/40 text-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {diag.sms.status === 'verified' ? <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                          <div className="flex-1">
                            <p className="font-bold mb-0.5">
                              SMS Sender {diag.sms.number && <span className="font-mono opacity-70">({diag.sms.number})</span>}
                              {diag.sms.error_code && <span className="ml-1.5 font-mono text-[9px] uppercase opacity-70">code {diag.sms.error_code}</span>}
                            </p>
                            <p className="leading-snug">{diag.sms.message}</p>
                            {diag.sms.remediation && (
                              <p className="leading-snug mt-1 pt-1 border-t border-current/10 opacity-90">
                                <strong>Fix:</strong> {diag.sms.remediation}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {(diag.incoming_numbers?.length > 0 || diag.whatsapp_senders?.length > 0) && (
                        <div className="text-[10px] text-muted-foreground/80 leading-snug pt-1">
                          {diag.incoming_numbers?.length > 0 && (
                            <p data-testid="preflight-incoming-list">
                              <strong>Twilio-owned numbers ({diag.incoming_numbers.length}):</strong>{' '}
                              <span className="font-mono">{diag.incoming_numbers.slice(0, 5).join(', ')}{diag.incoming_numbers.length > 5 ? ', …' : ''}</span>
                            </p>
                          )}
                          {diag.whatsapp_senders?.length > 0 && (
                            <p data-testid="preflight-wa-senders-list">
                              <strong>Approved WhatsApp senders ({diag.whatsapp_senders.length}):</strong>{' '}
                              <span className="font-mono">{diag.whatsapp_senders.join(', ')}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Button onClick={handleSaveWhatsApp} disabled={platformSaving} className="w-full" data-testid="save-whatsapp-btn">
                  <Save className="h-4 w-4 mr-2" /> {platformSaving ? 'Saving...' : 'Save Twilio Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* Phase 291 — AiSensy gateway DECOMMISSIONED. The centralised
                Meta WhatsApp relay (services/whatsapp_relay.py) now handles
                every outbound message via approved templates. The legacy
                AiSensyConfigCard import has been removed; the per-academy
                aisensy_* fields will be purged by the irreversible schema
                migration script (scripts/purge_legacy_telephony_schema.py). */}

            {/* Phase 266k/266l — Meta WhatsApp Cloud config + Send-test block, MIRRORED here
                so SAs find it from the WhatsApp tab too. Saves write to the same encrypted
                platform_settings document as the Meta Ads tab — `handleSaveMeta` is the
                single source of truth, so configuring it from either tab stays consistent. */}
            <Card className="max-w-2xl mt-6" data-testid="meta-whatsapp-mirror-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" /> Meta WhatsApp Cloud (Global Relay)
                  </span>
                  {metaStatus?.meta_whatsapp_ready_for_live ? (
                    <span className="text-[10px] text-emerald-600 font-mono inline-flex items-center gap-1" data-testid="meta-whatsapp-ready-pill-wa">
                      <CheckCircle className="h-3 w-3" /> Live
                    </span>
                  ) : (
                    <span className="text-[10px] text-amber-600 font-mono" data-testid="meta-whatsapp-pending-pill-wa">
                      Pending config
                    </span>
                  )}
                </CardTitle>
                <p className="text-xs text-muted-foreground leading-relaxed pt-1">
                  Drives the platform-shared Meta Cloud WhatsApp relay. Token is Fernet-encrypted at rest; numeric IDs are stored plaintext. <strong>Same backing store as the Meta Ads tab</strong> — change once, takes effect everywhere.
                </p>
              </CardHeader>
              <CardContent className="space-y-4" data-testid="meta-whatsapp-config-section-wa">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Phone Number ID</Label>
                    <Input
                      value={metaForm.meta_whatsapp_phone_number_id}
                      onChange={(e) => setMetaForm({...metaForm, meta_whatsapp_phone_number_id: e.target.value})}
                      placeholder="109876543210987"
                      data-testid="meta-whatsapp-phone-id-input-wa"
                    />
                    <p className="text-[10px] text-muted-foreground">From Meta Business Suite → WhatsApp → API Setup.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">WABA ID</Label>
                    <Input
                      value={metaForm.meta_waba_id}
                      onChange={(e) => setMetaForm({...metaForm, meta_waba_id: e.target.value})}
                      placeholder="123456789012345"
                      data-testid="meta-waba-id-input-wa"
                    />
                    <p className="text-[10px] text-muted-foreground">WhatsApp Business Account ID (template management).</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    Permanent WhatsApp Token
                    {metaStatus?.meta_whatsapp_token_preview && <span className="text-[10px] text-emerald-600 font-mono">on-file: {metaStatus.meta_whatsapp_token_preview}</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showMetaWhatsAppToken ? 'text' : 'password'}
                      value={metaForm.meta_whatsapp_token}
                      onChange={(e) => setMetaForm({...metaForm, meta_whatsapp_token: e.target.value})}
                      placeholder={metaStatus?.meta_whatsapp_token_preview ? 'Leave empty to keep existing' : 'Paste system-user permanent token'}
                      data-testid="meta-whatsapp-token-input-wa"
                    />
                    <button type="button" onClick={() => setShowMetaWhatsAppToken(!showMetaWhatsAppToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="meta-whatsapp-token-toggle-wa">
                      {showMetaWhatsAppToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Use a <strong>System User</strong> permanent token (never a 24-hour user token) with <code className="bg-muted px-1 rounded">whatsapp_business_messaging</code> + <code className="bg-muted px-1 rounded">whatsapp_business_management</code> scopes.
                  </p>
                </div>

                <Button
                  onClick={handleSaveMeta}
                  disabled={metaSaving || metaHealthChecking}
                  className="w-full"
                  data-testid="meta-whatsapp-save-btn-wa"
                >
                  {metaSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {metaSaving ? 'Saving…' : 'Save Meta WhatsApp Settings'}
                </Button>

                {/* Phase 266l — Send test WhatsApp (mirrored) */}
                <div className="rounded-lg border border-dashed border-emerald-300 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/10 p-3 space-y-3" data-testid="meta-whatsapp-test-block-wa">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">Send test WhatsApp</p>
                    <span className="text-[10px] text-muted-foreground">Fires a live Meta Graph API call · billable</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Verify the relay end-to-end with a Meta-approved template. Refuses to send if credentials are missing.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="space-y-1 sm:col-span-1">
                      <Label className="text-[11px]">Recipient (E.164)</Label>
                      <Input
                        value={metaWhatsAppTestForm.to_number}
                        onChange={(e) => setMetaWhatsAppTestForm({...metaWhatsAppTestForm, to_number: e.target.value})}
                        placeholder="+919876543210"
                        data-testid="meta-whatsapp-test-to-input-wa"
                      />
                    </div>
                    <div className="space-y-1 sm:col-span-1">
                      <Label className="text-[11px]">Template name</Label>
                      <Input
                        value={metaWhatsAppTestForm.template_name}
                        onChange={(e) => setMetaWhatsAppTestForm({...metaWhatsAppTestForm, template_name: e.target.value})}
                        placeholder="hello_world"
                        data-testid="meta-whatsapp-test-template-input-wa"
                      />
                    </div>
                    <div className="space-y-1 sm:col-span-1">
                      <Label className="text-[11px]">Language code</Label>
                      <Input
                        value={metaWhatsAppTestForm.language_code}
                        onChange={(e) => setMetaWhatsAppTestForm({...metaWhatsAppTestForm, language_code: e.target.value})}
                        placeholder="en_US"
                        data-testid="meta-whatsapp-test-lang-input-wa"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleSendMetaWhatsAppTest}
                    disabled={sendingMetaWhatsAppTest}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    data-testid="meta-whatsapp-test-send-btn-wa"
                  >
                    {sendingMetaWhatsAppTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Phone className="h-4 w-4 mr-2" />}
                    {sendingMetaWhatsAppTest ? 'Sending…' : 'Send test WhatsApp'}
                  </Button>

                  {metaWhatsAppTestResult && (
                    <div
                      className={`rounded-md border p-2.5 text-[11px] font-mono leading-snug ${
                        metaWhatsAppTestResult.http_success && metaWhatsAppTestResult.payload?.ok
                          ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200'
                          : 'border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30 text-rose-800 dark:text-rose-200'
                      }`}
                      data-testid="meta-whatsapp-test-result-wa"
                    >
                      {metaWhatsAppTestResult.http_success ? (
                        metaWhatsAppTestResult.payload?.ok ? (
                          <>
                            <strong>Sent ✓</strong> · message_id: {metaWhatsAppTestResult.payload.message_id || 'n/a'}
                            {metaWhatsAppTestResult.payload.request_id && <> · request_id: {metaWhatsAppTestResult.payload.request_id}</>}
                          </>
                        ) : (
                          <>
                            <strong>Meta rejected the send</strong>
                            {metaWhatsAppTestResult.payload?.status ? <> · HTTP {metaWhatsAppTestResult.payload.status}</> : null}
                            <br />
                            {metaWhatsAppTestResult.payload?.error || 'Unknown error from Meta'}
                          </>
                        )
                      ) : (
                        <>
                          <strong>{metaWhatsAppTestResult.status === 412 ? 'Save credentials first' : 'Test send failed'}</strong>
                          {metaWhatsAppTestResult.status ? <> · HTTP {metaWhatsAppTestResult.status}</> : null}
                          <br />
                          {metaWhatsAppTestResult.detail}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Phase 288 — Dynamic WhatsApp Template Routing (SA-only) */}
            <div className="max-w-2xl mt-4">
              <WhatsAppTemplateRoutingCard />
            </div>
          </TabsContent>

          {/* Phase 68 — GST & Business details Tab */}
          <TabsContent value="gst">
            <Card className="max-w-2xl" data-testid="gst-business-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" /> Out-Play Legal Entity (GST)
                </CardTitle>
                <CardDescription>These details print at the top of every Tax Invoice and Receipt sent to academies.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Legal Entity Name</Label>
                  <Input value={gstForm.gst_legal_name} onChange={(e) => setGstForm({...gstForm, gst_legal_name: e.target.value})} placeholder="Out-Play Technologies Pvt Ltd" data-testid="gst-legal-name-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">GSTIN</Label>
                    <Input value={gstForm.gst_gstin} onChange={(e) => setGstForm({...gstForm, gst_gstin: e.target.value.toUpperCase()})} placeholder="37AAACI1234A1ZP" data-testid="gst-gstin-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">HSN / SAC Code</Label>
                    <Input value={gstForm.gst_hsn_sac} onChange={(e) => setGstForm({...gstForm, gst_hsn_sac: e.target.value})} placeholder="998314" data-testid="gst-hsn-sac-input" />
                    <p className="text-[10px] text-muted-foreground">Default: 998314 (IT software services)</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Registered Address</Label>
                  <textarea
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px]"
                    value={gstForm.gst_address}
                    onChange={(e) => setGstForm({...gstForm, gst_address: e.target.value})}
                    placeholder="Street, City, State, PIN"
                    data-testid="gst-address-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">PAN (optional)</Label>
                  <Input value={gstForm.gst_pan} onChange={(e) => setGstForm({...gstForm, gst_pan: e.target.value.toUpperCase()})} placeholder="AAACI1234A" data-testid="gst-pan-input" />
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Where this appears:</strong> Tax Invoice PDF header, Receipt PDF header, plus the email signature on every invoice/reminder/payment-success notification routed through the Fail-Safe Relay.
                  </p>
                </div>
                <Button onClick={handleSaveGst} disabled={savingGst} className="w-full" data-testid="save-gst-btn">
                  <Save className="h-4 w-4 mr-2" /> {savingGst ? 'Saving…' : 'Save Business Details'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Phase 89B.2 — Meta Ads Platform Integration (Super Admin only) */}
          <TabsContent value="meta">
            <Card className="max-w-3xl" data-testid="meta-ads-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Hexagon className="h-5 w-5 text-pink-500" /> Meta Ads Platform Integration
                </CardTitle>
                <CardDescription>
                  Powers SocialBee organic + paid publishing to Facebook &amp; Instagram. Secrets are AES-encrypted at rest (Fernet).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Status pill */}
                <div className="rounded-lg border bg-gradient-to-br from-amber-50 to-pink-50 dark:from-amber-950/20 dark:to-pink-950/20 p-3" data-testid="meta-status-pill">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {metaStatus?.ready_for_live ? (
                        <><CheckCircle className="h-4 w-4 text-emerald-600" /><span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Ready for live publishing</span></>
                      ) : (
                        <><AlertTriangle className="h-4 w-4 text-amber-600" /><span className="text-sm font-medium text-amber-700 dark:text-amber-300">Credentials missing — SocialBee in simulation mode</span></>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={fetchMetaStatus} disabled={metaLoading} data-testid="meta-refresh-btn">
                      <RefreshCw className={`h-3.5 w-3.5 ${metaLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  {/* Phase 89B.2.2 — Last-verified line */}
                  <p className="text-[11px] text-muted-foreground mt-1.5 pl-6" data-testid="meta-last-verified">
                    {metaStatus?.meta_last_verified_at ? (
                      <>
                        Last verified:{' '}
                        <span className={metaStatus.meta_last_verified_ok ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-rose-700 dark:text-rose-400 font-medium'}>
                          {formatRelativeTime(metaStatus.meta_last_verified_at)}
                        </span>
                        {' '}<span className="font-mono text-[10px]" title={new Date(metaStatus.meta_last_verified_at).toString()}>
                          ({new Date(metaStatus.meta_last_verified_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })} IST)
                        </span>
                        {metaStatus.meta_last_verified_ok && metaStatus.meta_last_verified_latency_ms != null && (
                          <span className="text-emerald-600 dark:text-emerald-400 font-mono ml-1.5">· {metaStatus.meta_last_verified_latency_ms} ms</span>
                        )}
                        {!metaStatus.meta_last_verified_ok && metaStatus.meta_last_verified_status != null && (
                          <span className="text-rose-600 dark:text-rose-400 font-mono ml-1.5">· HTTP {metaStatus.meta_last_verified_status}</span>
                        )}
                      </>
                    ) : (
                      <>Last verified: <span className="italic">Never</span> — run health check to validate credentials.</>
                    )}
                  </p>
                </div>

                {/* Public IDs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Meta App ID</Label>
                    <Input
                      value={metaForm.meta_app_id}
                      onChange={(e) => setMetaForm({...metaForm, meta_app_id: e.target.value})}
                      placeholder="123456789012345"
                      data-testid="meta-app-id-input"
                    />
                    <p className="text-[10px] text-muted-foreground">developers.facebook.com → My Apps → App ID</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Business Portfolio ID</Label>
                    <Input
                      value={metaForm.meta_business_portfolio_id}
                      onChange={(e) => setMetaForm({...metaForm, meta_business_portfolio_id: e.target.value})}
                      placeholder="987654321098765"
                      data-testid="meta-portfolio-input"
                    />
                    <p className="text-[10px] text-muted-foreground">business.facebook.com → Business Settings → Business Info</p>
                  </div>
                </div>

                {/* Secrets */}
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    App Secret
                    {metaStatus?.meta_app_secret_preview && <span className="text-[10px] text-emerald-600 font-mono">on-file: {metaStatus.meta_app_secret_preview}</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showMetaAppSecret ? 'text' : 'password'}
                      value={metaForm.meta_app_secret}
                      onChange={(e) => setMetaForm({...metaForm, meta_app_secret: e.target.value})}
                      placeholder={metaStatus?.meta_app_secret_preview ? 'Leave empty to keep existing' : 'Paste App Secret'}
                      data-testid="meta-app-secret-input"
                    />
                    <button type="button" onClick={() => setShowMetaAppSecret(!showMetaAppSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showMetaAppSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    System User Access Token
                    {metaStatus?.meta_system_user_token_preview && <span className="text-[10px] text-emerald-600 font-mono">on-file: {metaStatus.meta_system_user_token_preview}</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showMetaSystemToken ? 'text' : 'password'}
                      value={metaForm.meta_system_user_token}
                      onChange={(e) => setMetaForm({...metaForm, meta_system_user_token: e.target.value})}
                      placeholder={metaStatus?.meta_system_user_token_preview ? 'Leave empty to keep existing' : 'Paste long-lived System User token'}
                      data-testid="meta-system-token-input"
                    />
                    <button type="button" onClick={() => setShowMetaSystemToken(!showMetaSystemToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showMetaSystemToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Required scopes: <code className="bg-muted px-1 rounded">ads_management</code>, <code className="bg-muted px-1 rounded">business_management</code>, <code className="bg-muted px-1 rounded">pages_manage_posts</code>.</p>
                </div>

                <Separator />

                {/* Default targets */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Default Publishing Targets</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Ad Account ID</Label>
                      <Input
                        value={metaForm.meta_default_ad_account_id}
                        onChange={(e) => setMetaForm({...metaForm, meta_default_ad_account_id: e.target.value})}
                        placeholder="act_1234567890"
                        data-testid="meta-ad-account-input"
                      />
                      <p className="text-[10px] text-muted-foreground">Must start with <code>act_</code></p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Facebook Page ID</Label>
                      <Input
                        value={metaForm.meta_default_page_id}
                        onChange={(e) => setMetaForm({...metaForm, meta_default_page_id: e.target.value})}
                        placeholder="100123456789012"
                        data-testid="meta-page-id-input"
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs">Instagram Business Account ID</Label>
                      <Input
                        value={metaForm.meta_default_ig_business_account_id}
                        onChange={(e) => setMetaForm({...metaForm, meta_default_ig_business_account_id: e.target.value})}
                        placeholder="17841405822304914"
                        data-testid="meta-ig-business-input"
                      />
                      <p className="text-[10px] text-muted-foreground">Required for the Instagram 2-step publish flow. Find it via <code className="bg-muted px-1 rounded">/{`{page_id}`}?fields=instagram_business_account</code> or your Business Suite settings.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    Page Access Token
                    {metaStatus?.meta_default_page_access_token_preview && <span className="text-[10px] text-emerald-600 font-mono">on-file: {metaStatus.meta_default_page_access_token_preview}</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showMetaPageToken ? 'text' : 'password'}
                      value={metaForm.meta_default_page_access_token}
                      onChange={(e) => setMetaForm({...metaForm, meta_default_page_access_token: e.target.value})}
                      placeholder={metaStatus?.meta_default_page_access_token_preview ? 'Leave empty to keep existing' : 'Paste Page access token'}
                      data-testid="meta-page-token-input"
                    />
                    <button type="button" onClick={() => setShowMetaPageToken(!showMetaPageToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showMetaPageToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Distinct from the System User token — Meta scopes Page operations to the Page's own token.</p>
                </div>

                <Separator />

                {/* Phase 266k — WhatsApp Cloud Channel (platform-shared transport) */}
                <div data-testid="meta-whatsapp-config-section">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">WhatsApp Cloud Channel</p>
                    {metaStatus?.meta_whatsapp_ready_for_live ? (
                      <span className="text-[10px] text-emerald-600 font-mono inline-flex items-center gap-1" data-testid="meta-whatsapp-ready-pill">
                        <CheckCircle className="h-3 w-3" /> Live
                      </span>
                    ) : (
                      <span className="text-[10px] text-amber-600 font-mono" data-testid="meta-whatsapp-pending-pill">
                        Pending config
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
                    Drives the platform-shared WhatsApp relay (<code className="bg-muted px-1 rounded">meta_whatsapp.dispatch_global_whatsapp_template</code>). When unset, transactional WhatsApp falls back to mock mode. The token is Fernet-encrypted at rest; IDs are stored plaintext (Meta treats them as public).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Phone Number ID</Label>
                      <Input
                        value={metaForm.meta_whatsapp_phone_number_id}
                        onChange={(e) => setMetaForm({...metaForm, meta_whatsapp_phone_number_id: e.target.value})}
                        placeholder="109876543210987"
                        data-testid="meta-whatsapp-phone-id-input"
                      />
                      <p className="text-[10px] text-muted-foreground">From Meta Business Suite → WhatsApp → API Setup.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">WABA ID</Label>
                      <Input
                        value={metaForm.meta_waba_id}
                        onChange={(e) => setMetaForm({...metaForm, meta_waba_id: e.target.value})}
                        placeholder="123456789012345"
                        data-testid="meta-waba-id-input"
                      />
                      <p className="text-[10px] text-muted-foreground">WhatsApp Business Account ID (used for template management).</p>
                    </div>
                  </div>
                  <div className="space-y-1.5 mt-3">
                    <Label className="text-xs flex items-center gap-1.5">
                      Permanent WhatsApp Token
                      {metaStatus?.meta_whatsapp_token_preview && <span className="text-[10px] text-emerald-600 font-mono">on-file: {metaStatus.meta_whatsapp_token_preview}</span>}
                    </Label>
                    <div className="relative">
                      <Input
                        type={showMetaWhatsAppToken ? 'text' : 'password'}
                        value={metaForm.meta_whatsapp_token}
                        onChange={(e) => setMetaForm({...metaForm, meta_whatsapp_token: e.target.value})}
                        placeholder={metaStatus?.meta_whatsapp_token_preview ? 'Leave empty to keep existing' : 'Paste system-user permanent token'}
                        data-testid="meta-whatsapp-token-input"
                      />
                      <button type="button" onClick={() => setShowMetaWhatsAppToken(!showMetaWhatsAppToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="meta-whatsapp-token-toggle">
                        {showMetaWhatsAppToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Use a <strong>System User</strong> permanent token (never a 24-hour user token) with <code className="bg-muted px-1 rounded">whatsapp_business_messaging</code> + <code className="bg-muted px-1 rounded">whatsapp_business_management</code> scopes.
                    </p>
                  </div>

                  {/* Phase 266l — Send Test WhatsApp */}
                  <div className="mt-4 rounded-lg border border-dashed border-emerald-300 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/10 p-3 space-y-3" data-testid="meta-whatsapp-test-block">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">Send test WhatsApp</p>
                      <span className="text-[10px] text-muted-foreground">Fires a live Meta Graph API call · billable</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Verify the relay end-to-end with a Meta-approved template before real notifications go out. Refuses to send if credentials are missing or invalid.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="space-y-1 sm:col-span-1">
                        <Label className="text-[11px]">Recipient (E.164)</Label>
                        <Input
                          value={metaWhatsAppTestForm.to_number}
                          onChange={(e) => setMetaWhatsAppTestForm({...metaWhatsAppTestForm, to_number: e.target.value})}
                          placeholder="+919876543210"
                          data-testid="meta-whatsapp-test-to-input"
                        />
                      </div>
                      <div className="space-y-1 sm:col-span-1">
                        <Label className="text-[11px]">Template name</Label>
                        <Input
                          value={metaWhatsAppTestForm.template_name}
                          onChange={(e) => setMetaWhatsAppTestForm({...metaWhatsAppTestForm, template_name: e.target.value})}
                          placeholder="hello_world"
                          data-testid="meta-whatsapp-test-template-input"
                        />
                      </div>
                      <div className="space-y-1 sm:col-span-1">
                        <Label className="text-[11px]">Language code</Label>
                        <Input
                          value={metaWhatsAppTestForm.language_code}
                          onChange={(e) => setMetaWhatsAppTestForm({...metaWhatsAppTestForm, language_code: e.target.value})}
                          placeholder="en_US"
                          data-testid="meta-whatsapp-test-lang-input"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleSendMetaWhatsAppTest}
                      disabled={sendingMetaWhatsAppTest}
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      data-testid="meta-whatsapp-test-send-btn"
                    >
                      {sendingMetaWhatsAppTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Phone className="h-4 w-4 mr-2" />}
                      {sendingMetaWhatsAppTest ? 'Sending…' : 'Send test WhatsApp'}
                    </Button>

                    {metaWhatsAppTestResult && (
                      <div
                        className={`rounded-md border p-2.5 text-[11px] font-mono leading-snug ${
                          metaWhatsAppTestResult.http_success && metaWhatsAppTestResult.payload?.ok
                            ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200'
                            : 'border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30 text-rose-800 dark:text-rose-200'
                        }`}
                        data-testid="meta-whatsapp-test-result"
                      >
                        {metaWhatsAppTestResult.http_success ? (
                          metaWhatsAppTestResult.payload?.ok ? (
                            <>
                              <strong>Sent ✓</strong> · message_id: {metaWhatsAppTestResult.payload.message_id || 'n/a'}
                              {metaWhatsAppTestResult.payload.request_id && <> · request_id: {metaWhatsAppTestResult.payload.request_id}</>}
                            </>
                          ) : (
                            <>
                              <strong>Meta rejected the send</strong>
                              {metaWhatsAppTestResult.payload?.status ? <> · HTTP {metaWhatsAppTestResult.payload.status}</> : null}
                              <br />
                              {metaWhatsAppTestResult.payload?.error || 'Unknown error from Meta'}
                            </>
                          )
                        ) : (
                          <>
                            <strong>{metaWhatsAppTestResult.status === 412 ? 'Save credentials first' : 'Test send failed'}</strong>
                            {metaWhatsAppTestResult.status ? <> · HTTP {metaWhatsAppTestResult.status}</> : null}
                            <br />
                            {metaWhatsAppTestResult.detail}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action row */}
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <Button onClick={handleSaveMeta} disabled={metaSaving || metaHealthChecking} className="flex-1" data-testid="meta-save-btn">
                    {metaSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {metaSaving
                      ? (metaHealthChecking ? 'Verifying with Meta…' : 'Saving…')
                      : 'Save & Verify'}
                  </Button>
                  <Button
                    onClick={handleMetaHealthCheck}
                    disabled={metaHealthChecking || metaSaving || !metaStatus?.ready_for_live}
                    variant="outline"
                    className="flex-1"
                    data-testid="meta-health-check-btn"
                    title={!metaStatus?.ready_for_live ? 'Save App ID + System User Token first' : 'Re-run the health probe without saving'}
                  >
                    {metaHealthChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
                    {metaHealthChecking ? 'Pinging Graph API…' : 'Re-run Health Check'}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground -mt-1">
                  <strong>Save & Verify</strong> stores your credentials (Fernet-encrypted at rest) and immediately fires a live <code className="bg-muted px-1 rounded">/v19.0</code> probe against the Business Portfolio so you see real Meta status, latency, or OAuth error in one click.
                </p>

                {/* Health result panel */}
                {metaHealth && (
                  <div
                    className={`rounded-lg border p-3 ${metaHealth.ok ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20' : 'border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/20'}`}
                    data-testid="meta-health-result"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      {metaHealth.ok
                        ? <CheckCircle className="h-4 w-4 text-emerald-600" />
                        : <XCircle className="h-4 w-4 text-rose-600" />}
                      <p className={`text-sm font-semibold ${metaHealth.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                        {metaHealth.ok ? 'Healthy' : 'Connection Failure'}
                        {metaHealth.ok && metaHealth.latency_ms != null && <span className="ml-2 text-xs font-mono text-emerald-600">{metaHealth.latency_ms} ms</span>}
                        {!metaHealth.ok && metaHealth.status != null && <span className="ml-2 text-xs font-mono text-rose-600">HTTP {metaHealth.status}</span>}
                      </p>
                    </div>
                    {metaHealth.ok && metaHealth.business_portfolio && (
                      <p className="text-xs text-emerald-700 dark:text-emerald-300 font-mono">
                        Portfolio: {metaHealth.business_portfolio?.name || metaHealth.business_portfolio?.id || 'OK'}
                      </p>
                    )}
                    {!metaHealth.ok && (
                      <p className="text-[11px] text-rose-700 dark:text-rose-300 font-mono whitespace-pre-wrap break-all leading-snug">
                        {metaHealth.reason || metaHealth.error || 'Unknown error'}
                      </p>
                    )}
                  </div>
                )}

                {/* Footer hint */}
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg">
                  <p className="text-[11px] text-blue-800 dark:text-blue-200 leading-relaxed">
                    <strong>How it flows:</strong> Save creds here → <em>SocialBee</em> publish (Legend tier) → <code className="bg-white/60 dark:bg-slate-900/60 px-1 rounded">_socialbee_run_meta_facebook</code> worker calls Graph API v19.0 with <code className="bg-white/60 dark:bg-slate-900/60 px-1 rounded">OUTCOME_ENGAGEMENT</code> + <code className="bg-white/60 dark:bg-slate-900/60 px-1 rounded">special_ad_categories: []</code>. Full HTTP trace lands in <code className="bg-white/60 dark:bg-slate-900/60 px-1 rounded">platform_execution_jobs</code> for debugging.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Phase 325b — Auto-Bill Settings */}
          <TabsContent value="billing">
            <div className="max-w-2xl">
              <BillingSettingsCard />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ============================================================
  // ACADEMY OWNER VIEW - Full settings with all tabs
  // ============================================================
  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-display font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage academy configuration</p>
      </div>

      <Tabs defaultValue="academy">
        {/* Phase 96 — Polished horizontal tab deck. Stripped overlap with Wall of Champions
            (moved to its own widget below). Spacing tightened to a strict design grid. */}
        <TabsList
          className="w-full h-auto flex flex-wrap items-center gap-2 bg-transparent border-b border-slate-200 dark:border-slate-800 rounded-none p-0 pb-3 justify-start"
          data-testid="settings-tabs-list"
        >
          <TabsTrigger value="academy" data-testid="tab-academy" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all"><Building2 className="h-3.5 w-3.5 mr-1.5" /> Academy Info</TabsTrigger>
          <TabsTrigger value="centers" data-testid="tab-centers" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all"><MapPin className="h-3.5 w-3.5 mr-1.5" /> Centers</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all"><BookOpen className="h-3.5 w-3.5 mr-1.5" /> Courses</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all"><Users className="h-3.5 w-3.5 mr-1.5" /> Batches</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all"><Shield className="h-3.5 w-3.5 mr-1.5" /> Departments</TabsTrigger>
          {/* Phase 308 — Geofence configuration tab */}
          <TabsTrigger value="geofence" data-testid="tab-geofence" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all"><MapPin className="h-3.5 w-3.5 mr-1.5" /> Geofence</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all"><IndianRupee className="h-3.5 w-3.5 mr-1.5" /> Payments</TabsTrigger>
          {/* Phase 312 — Billing & ROI: surface subscription_price_inr for the AI Recovery ROI tile */}
          <TabsTrigger value="billing-roi" data-testid="tab-billing-roi" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Billing &amp; ROI</TabsTrigger>
          <TabsTrigger value="integrations" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all" data-testid="tab-integrations"><Zap className="h-3.5 w-3.5 mr-1.5" /> Integrations &amp; Comms</TabsTrigger>
          <TabsTrigger value="webhooks" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all" data-testid="tab-webhooks"><Webhook className="h-3.5 w-3.5 mr-1.5" /> Webhooks</TabsTrigger>
          {/* Phase 300 — Automation Flag panel */}
          <TabsTrigger value="automation" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all" data-testid="tab-automation"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Automation</TabsTrigger>
          <TabsTrigger value="privacy" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium transition-all" data-testid="tab-privacy"><Shield className="h-3.5 w-3.5 mr-1.5" /> Privacy</TabsTrigger>
        </TabsList>

        {/* Academy Info Tab */}
        <TabsContent value="academy">
          <div className="space-y-4 max-w-2xl">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Academy Logo</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="h-20 w-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-muted/30 overflow-hidden">
                    {logoUrl ? <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" /> : <Building2 className="h-8 w-8 text-muted-foreground/40" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <p className="text-sm text-muted-foreground">Upload your academy logo. Recommended: 200x200px, PNG or JPG</p>
                    <Input type="file" accept="image/*" className="text-xs" disabled={uploading} onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return; setUploading(true);
                      try {
                        const fd = new FormData(); fd.append('file', file);
                        const res = await api.post('/academy/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setLogoUrl(res.data.logo_url);
                        // Phase 122 — Reactive header avatar sync.  The AppShell
                        // listens to this custom event and re-renders the top-right
                        // roundel without a hard page reload.
                        window.dispatchEvent(new CustomEvent('outplay:profile-photo-updated', {
                          detail: { url: res.data.logo_url, source: 'academy_logo' },
                        }));
                        toast.success('Logo uploaded!');
                      } catch (err) { toast.error('Upload failed'); }
                      finally { setUploading(false); }
                    }} />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Academy Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Academy Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
                {/* Phase 297 — Google Reviews URL for the parent NPS engine */}
                <div className="space-y-1.5" data-testid="google-review-url-row">
                  <Label className="text-xs flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                    Google Reviews URL
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">growth</span>
                  </Label>
                  <Input
                    type="url"
                    placeholder="https://g.page/r/your-academy/review"
                    value={form.google_review_url || ''}
                    onChange={(e) => setForm({...form, google_review_url: e.target.value})}
                    data-testid="google-review-url-input"
                  />
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Pasted from <em>Google Business Profile → Get more reviews</em>. When set, parents who rate 4-5★ post-session are
                    redirected here to leave a public review. 1-3★ feedback stays private with you.
                  </p>
                  {/* Phase 298 — entry point to the AI Reputation Recovery Inbox */}
                  <a
                    href="/recovery-inbox"
                    className="inline-flex items-center gap-1.5 text-[11px] text-rose-700 hover:text-rose-800 underline underline-offset-4 mt-1"
                    data-testid="recovery-inbox-link"
                  >
                    🤖 Open Reputation Recovery Inbox →
                  </a>
                </div>
                <Button onClick={handleSave} disabled={saving} className="w-full"><Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Academy Settings'}</Button>
              </CardContent>
            </Card>
            {/* Phase 100 — Auto-seed sport-themed SVG avatars for newly-onboarded students */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Onboarding Preferences</CardTitle></CardHeader>
              <CardContent>
                <label className="flex items-start justify-between gap-4 py-1 cursor-pointer" data-testid="auto-seed-avatars-row">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">Auto-assign sport avatars to new students</p>
                    <p className="text-xs text-muted-foreground">When ON, every new student gets a random sport-matching 3D badge as their initial avatar (Hockey kids get hockey badges, etc.). Students can change theirs anytime.</p>
                  </div>
                  <Switch
                    checked={form.auto_seed_avatars !== false}
                    onCheckedChange={async (val) => {
                      setForm(prev => ({ ...prev, auto_seed_avatars: val }));
                      try {
                        await api.put('/academy/preferences', { auto_seed_avatars: val });
                        toast.success(`Avatar auto-seeding ${val ? 'enabled' : 'disabled'}`);
                      } catch (err) {
                        // Revert local state on failure so the toggle reflects reality.
                        setForm(prev => ({ ...prev, auto_seed_avatars: !val }));
                        toast.error('Failed to update preference');
                      }
                    }}
                    data-testid="auto-seed-avatars-switch"
                  />
                </label>
              </CardContent>
            </Card>
            {/* Phase 206 — Finance Preferences. Server-enforced cap for the AI
                Discount Co-Pilot. Defaults to 25%; clamped to [0, 100] both
                client- and server-side. The Co-Pilot endpoint reads this on
                every call and uses it both as the LLM-side instruction AND as
                the server-side hard ceiling — so a hallucinated 90% can never
                reach the operator. */}
            <Card data-testid="finance-preferences-card">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><IndianRupee className="h-4 w-4 text-primary" /> Finance Preferences</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4 py-1" data-testid="max-scholarship-pct-row">
                  <div className="space-y-0.5 flex-1">
                    <p className="text-sm font-medium">Max scholarship cap (AI Co-Pilot)</p>
                    <p className="text-xs text-muted-foreground">The AI Discount Co-Pilot in the Student modal will never recommend a discount above this percentage of the course base fee. Hardcap enforced on the server.</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number" min="0" max="100" step="1"
                      value={form.max_scholarship_pct ?? 25}
                      onChange={(e) => setForm(prev => ({ ...prev, max_scholarship_pct: e.target.value }))}
                      onBlur={async (e) => {
                        const raw = parseFloat(e.target.value);
                        const next = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 25;
                        setForm(prev => ({ ...prev, max_scholarship_pct: next }));
                        try {
                          await api.put('/academy/preferences', { max_scholarship_pct: next });
                          toast.success(`Co-Pilot cap saved at ${next}%`);
                        } catch (err) {
                          toast.error('Failed to save scholarship cap');
                        }
                      }}
                      className="w-20 text-right"
                      data-testid="max-scholarship-pct-input"
                    />
                    <span className="text-sm font-semibold text-muted-foreground">%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Phase 96 — Wall of Champions widget embedded under Academy Info (no longer competing for tab-deck real estate). */}
            <WallOfChampionsTab />
          </div>
        </TabsContent>

        {/* Centers Tab */}
        <TabsContent value="centers">
          <div className="space-y-4 max-w-3xl">
            <div className="flex justify-end">
              <Dialog open={showAddCenter} onOpenChange={setShowAddCenter}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Center</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Add Center</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddCenter} className="space-y-3">
                    <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={centerForm.name} onChange={(e) => setCenterForm({...centerForm, name: e.target.value})} required /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Address</Label><Input value={centerForm.address} onChange={(e) => setCenterForm({...centerForm, address: e.target.value})} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Capacity</Label><Input type="number" value={centerForm.capacity} onChange={(e) => setCenterForm({...centerForm, capacity: e.target.value})} /></div>
                    <Button type="submit" className="w-full">Add Center</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Center</TableHead><TableHead className="hidden md:table-cell">Address</TableHead><TableHead>Capacity</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader><TableBody>
              {centers.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-12 text-muted-foreground">No centers configured.</TableCell></TableRow>
              : centers.map(c => <TableRow key={c.id}><TableCell className="font-medium">{c.name}</TableCell><TableCell className="hidden md:table-cell text-sm text-muted-foreground">{c.address||'-'}</TableCell><TableCell className="tabular-nums">{c.capacity||'-'}</TableCell><TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async()=>{await api.delete(`/academy/centers/${c.id}`);fetchConfig();}}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell></TableRow>)}
            </TableBody></Table></CardContent></Card>
          </div>
        </TabsContent>

        {/* Courses Tab */}
        <TabsContent value="courses">
          <div className="space-y-4 max-w-3xl">
            <div className="flex justify-end">
              <Dialog open={showAddCourse} onOpenChange={setShowAddCourse}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Course</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Add Course</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddCourse} className="space-y-3">
                    <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={courseForm.name} onChange={(e) => setCourseForm({...courseForm, name: e.target.value})} required /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Description</Label><Input value={courseForm.description} onChange={(e) => setCourseForm({...courseForm, description: e.target.value})} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label className="text-xs">Duration</Label><Input value={courseForm.duration} onChange={(e) => setCourseForm({...courseForm, duration: e.target.value})} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">Fees (₹)</Label><Input type="number" value={courseForm.fees} onChange={(e) => setCourseForm({...courseForm, fees: e.target.value})} /></div>
                    </div>
                    <Button type="submit" className="w-full">Add Course</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Course</TableHead><TableHead className="hidden md:table-cell">Duration</TableHead><TableHead>Fees</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader><TableBody>
              {courses.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-12 text-muted-foreground">No courses configured.</TableCell></TableRow>
              : courses.map(c => <TableRow key={c.id}><TableCell><div><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.description}</p></div></TableCell><TableCell className="hidden md:table-cell">{c.duration||'-'}</TableCell><TableCell className="font-semibold tabular-nums">₹{(c.fees||0).toLocaleString()}</TableCell><TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async()=>{await api.delete(`/academy/courses/${c.id}`);fetchConfig();}}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell></TableRow>)}
            </TableBody></Table></CardContent></Card>
          </div>
        </TabsContent>

        {/* Batches Tab */}
        <TabsContent value="batches">
          <div className="space-y-4 max-w-3xl">
            <div className="flex justify-end">
              <Dialog open={showAddBatch} onOpenChange={setShowAddBatch}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Batch</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Add Batch</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddBatch} className="space-y-3">
                    <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={batchForm.name} onChange={(e) => setBatchForm({...batchForm, name: e.target.value})} required /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label className="text-xs">Timing</Label><Input value={batchForm.timing} onChange={(e) => setBatchForm({...batchForm, timing: e.target.value})} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">Max Students</Label><Input type="number" value={batchForm.max_students} onChange={(e) => setBatchForm({...batchForm, max_students: e.target.value})} /></div>
                    </div>
                    <Button type="submit" className="w-full">Add Batch</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Batch</TableHead><TableHead>Timing</TableHead><TableHead>Max</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader><TableBody>
              {batches.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-12 text-muted-foreground">No batches configured.</TableCell></TableRow>
              : batches.map(b => <TableRow key={b.id}><TableCell className="font-medium">{b.name}</TableCell><TableCell>{b.timing||'-'}</TableCell><TableCell className="tabular-nums">{b.max_students||'-'}</TableCell><TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async()=>{await api.delete(`/academy/batches/${b.id}`);fetchConfig();}}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell></TableRow>)}
            </TableBody></Table></CardContent></Card>
          </div>
        </TabsContent>

        {/* Phase 96 — Departments Tab (operational departments → HRMS dropdown source) */}
        <TabsContent value="departments">
          <div className="space-y-4 max-w-3xl" data-testid="departments-tab">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Operational departments power the dropdown when onboarding coaches, trainers and admin staff in the HR Hub.</p>
              <Dialog open={showAddDept} onOpenChange={setShowAddDept}>
                <DialogTrigger asChild><Button size="sm" data-testid="add-department-btn"><Plus className="h-4 w-4 mr-1" /> Add Department</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Add Department</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddDept} className="space-y-3">
                    <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={deptForm.name} onChange={(e) => setDeptForm({...deptForm, name: e.target.value})} placeholder="e.g. Coaching, Operations, Finance" data-testid="department-name-input" required /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Description</Label><Input value={deptForm.description} onChange={(e) => setDeptForm({...deptForm, description: e.target.value})} placeholder="Short summary (optional)" /></div>
                    <Button type="submit" className="w-full" data-testid="department-submit-btn">Add Department</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Department</TableHead><TableHead className="hidden md:table-cell">Description</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader><TableBody>
              {departments.length === 0 ? <TableRow><TableCell colSpan={3} className="text-center py-12 text-muted-foreground" data-testid="departments-empty">No departments configured yet — add one to start onboarding staff against a canonical structure.</TableCell></TableRow>
              : departments.map(d => <TableRow key={d.id} data-testid={`department-row-${d.id}`}><TableCell className="font-medium">{d.name}</TableCell><TableCell className="hidden md:table-cell text-sm text-muted-foreground">{d.description||'-'}</TableCell><TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async()=>{await api.delete(`/academy/departments/${d.id}`);fetchConfig();}}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell></TableRow>)}
            </TableBody></Table></CardContent></Card>
          </div>
        </TabsContent>

        {/* Phase 308 — Geofence Location tab */}
        <TabsContent value="geofence">
          <div className="space-y-4 max-w-3xl">
            <GeofenceLocationCard />
          </div>
        </TabsContent>

        {/* Phase 312 — Billing & ROI tab. Sets the academy's monthly
            subscription price (₹) which powers the AI Recovery ROI hero
            tile multiplier on the owner dashboard. */}
        <TabsContent value="billing-roi" data-testid="tabs-content-billing-roi">
          <div className="space-y-4 max-w-2xl">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-emerald-600" />
                  Billing &amp; ROI Tracking
                </CardTitle>
                <CardDescription className="text-xs">
                  Configure your monthly subscription cost so the dashboard can
                  surface the exact Return on Investment when Out-Play&apos;s AI
                  recovers overdue fees.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="subscription-price-input">
                    Academy Monthly Subscription Cost (₹)
                  </Label>
                  <Input
                    id="subscription-price-input"
                    type="number"
                    min={0}
                    max={1_000_000}
                    inputMode="numeric"
                    value={form.subscription_price_inr ?? ''}
                    onChange={(e) => setForm({ ...form, subscription_price_inr: e.target.value })}
                    placeholder="e.g. 5000"
                    data-testid="subscription-price-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    We use this to calculate your exact Return on Investment (ROI)
                    on the main dashboard when Out-Play automatically recovers
                    overdue fees. Leave blank to default to ₹5,000.
                  </p>
                </div>

                <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-3 flex items-start gap-3">
                  <Info className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-emerald-900 space-y-1">
                    <p className="font-medium">How the ROI multiplier works</p>
                    <p>
                      Every fee that flips from <code className="px-1 rounded bg-white/60">pending → paid</code>
                      AFTER an AI win-back nudge counts as recovered revenue. The hero
                      tile on your dashboard then shows{' '}
                      <strong>recovered ÷ subscription = N×</strong>. A 5× multiplier
                      means Out-Play earned you 5× what you pay for it.
                    </p>
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="save-billing-roi-button"
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" /> Save Billing &amp; ROI</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Payments Tab (Academy) */}
        <TabsContent value="payments">
          <Card className="max-w-2xl">
            <CardHeader><CardTitle className="text-base">Payment Settings (Razorpay)</CardTitle><CardDescription>Configure for fee collection</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              {/* Razorpay setup guide — onboarding helper for non-technical academy owners */}
              <div
                className="bg-slate-50 dark:bg-slate-900/40 rounded-lg p-4 mb-2 border border-slate-200 dark:border-slate-800"
                data-testid="razorpay-setup-guide"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Info className="h-4 w-4 text-emerald-600" />
                  <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    How to find your Razorpay API Keys
                  </h4>
                </div>
                <ol className="space-y-2.5 text-sm text-slate-600 dark:text-slate-300">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                    <span>
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Log in &amp; Complete KYC:</span>{' '}
                      Log into your{' '}
                      <a
                        href="https://dashboard.razorpay.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-700 dark:text-emerald-400 underline underline-offset-2 hover:text-emerald-800"
                        data-testid="razorpay-guide-dashboard-link"
                      >
                        Razorpay Dashboard
                      </a>{' '}
                      and ensure your account is fully verified.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
                    <span>
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Switch to Live Mode:</span>{' '}
                      In the top menu of Razorpay, ensure the toggle is set to{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Live Mode</span>{' '}
                      (not Test Mode) so you can accept real payments.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center">3</span>
                    <span>
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Navigate to Keys:</span>{' '}
                      On the left sidebar, go to{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Account &amp; Settings</span>{' '}
                      →{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">API Keys</span>.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center">4</span>
                    <span>
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Generate &amp; Paste:</span>{' '}
                      Click{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Generate Key</span>.
                      Razorpay will show you a Key ID and Key Secret. Copy and paste them into the fields below.
                    </span>
                  </li>
                </ol>
              </div>

              <div className="space-y-1.5"><Label className="text-xs">Razorpay Key ID</Label><Input value={form.razorpay_key_id} onChange={(e) => setForm({...form, razorpay_key_id: e.target.value})} placeholder="rzp_live_xxx or rzp_test_xxx" data-testid="academy-rzp-key-id" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Razorpay Key Secret</Label>
                <div className="relative"><Input type={showSecretAcademy ? 'text' : 'password'} value={form.razorpay_key_secret} onChange={(e) => setForm({...form, razorpay_key_secret: e.target.value})} placeholder="Leave empty to keep existing" data-testid="academy-rzp-key-secret" />
                  <button type="button" onClick={() => setShowSecretAcademy(!showSecretAcademy)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><Eye className="h-4 w-4" /></button>
                </div>
              </div>
              {settings?.razorpay_configured && <div className="flex items-center gap-2 text-sm"><CheckCircle className="h-4 w-4 text-emerald-600" /><span className="text-emerald-600 font-medium">Razorpay configured</span></div>}

              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={handleSave} disabled={saving || rzpTestRunning} className="flex-1" data-testid="academy-rzp-save"><Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Payment Settings'}</Button>
                <Button
                  onClick={handleTestRazorpayConnection}
                  disabled={rzpTestRunning || saving || !settings?.razorpay_configured}
                  variant="outline"
                  className="flex-1"
                  data-testid="academy-rzp-test-connection"
                  title={!settings?.razorpay_configured ? 'Save Key ID + Secret first' : 'Probe Razorpay with the stored credentials'}
                >
                  {rzpTestRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
                  {rzpTestRunning ? 'Probing Razorpay…' : 'Test Gateway Connection'}
                </Button>
              </div>

              {/* Phase 90.2 — Test connection result */}
              {rzpTestResult && (
                <div
                  className={`rounded-lg border p-3 ${rzpTestResult.ok ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20' : 'border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/20'}`}
                  data-testid="academy-rzp-test-result"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    {rzpTestResult.ok
                      ? <CheckCircle className="h-4 w-4 text-emerald-600" />
                      : <XCircle className="h-4 w-4 text-rose-600" />}
                    <p className={`text-sm font-semibold ${rzpTestResult.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                      {rzpTestResult.ok ? 'Razorpay Authentication OK' : 'Razorpay Rejected Credentials'}
                      {rzpTestResult.latency_ms != null && <span className={`ml-2 text-xs font-mono ${rzpTestResult.ok ? 'text-emerald-600' : 'text-rose-600'}`}>{rzpTestResult.latency_ms} ms</span>}
                      {rzpTestResult.status != null && <span className={`ml-2 text-xs font-mono ${rzpTestResult.ok ? 'text-emerald-600' : 'text-rose-600'}`}>HTTP {rzpTestResult.status}</span>}
                    </p>
                  </div>
                  {rzpTestResult.ok ? (
                    <>
                      <p className="text-xs text-emerald-700 dark:text-emerald-300">{rzpTestResult.message}</p>
                      <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-0.5">{rzpTestResult.hint}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-rose-700 dark:text-rose-300 font-medium">{rzpTestResult.reason}</p>
                      <p className="text-[11px] text-rose-600 dark:text-rose-400 mt-1 leading-snug">{rzpTestResult.hint}</p>
                      {rzpTestResult.error && (
                        <pre className="font-mono text-[10px] mt-2 bg-white/60 dark:bg-slate-900/60 p-2 rounded whitespace-pre-wrap break-all max-h-32 overflow-auto text-rose-700 dark:text-rose-300">
                          {typeof rzpTestResult.error === 'string' ? rzpTestResult.error : JSON.stringify(rzpTestResult.error, null, 2)}
                        </pre>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Phase 293 — Razorpay Health Check Wizard (3-step post-config validation) */}
              <RazorpayHealthCheckWizard razorpayConfigured={!!settings?.razorpay_configured} />

              {/* Razorpay webhook configuration guide — final step so payment confirmations flow */}
              <div
                className="bg-indigo-50 dark:bg-indigo-950/30 rounded-lg p-4 border border-indigo-200 dark:border-indigo-900"
                data-testid="razorpay-webhook-setup-guide"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Webhook className="h-4 w-4 text-indigo-600" />
                  <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Final step: Configure your Razorpay Webhook
                  </h4>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 mb-3 leading-relaxed">
                  This lets Razorpay tell Out-Play the instant a parent finishes paying, so invoices update from <span className="font-mono bg-white dark:bg-slate-900 px-1 py-0.5 rounded text-[10px]">pending</span> to <span className="font-mono bg-white dark:bg-slate-900 px-1 py-0.5 rounded text-[10px]">paid</span> without any manual refresh.
                </p>
                <ol className="space-y-2.5 text-sm text-slate-600 dark:text-slate-300">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                    <span>
                      In your Razorpay Dashboard, go to{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Account &amp; Settings</span>{' '}
                      →{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Webhooks</span>{' '}
                      → click{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">+ Add New Webhook</span>.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
                    <span className="flex-1">
                      <div>
                        Paste this URL into the{' '}
                        <span className="font-semibold text-slate-800 dark:text-slate-100">Webhook URL</span>{' '}
                        field:
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <code
                          className="flex-1 font-mono text-[11px] bg-white dark:bg-slate-900 px-2 py-1.5 rounded border border-indigo-200 dark:border-indigo-800 break-all text-slate-800 dark:text-slate-200"
                          data-testid="razorpay-webhook-url-value"
                        >
                          {`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/webhooks/razorpay`}
                        </code>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 flex-shrink-0"
                          onClick={() => {
                            const url = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/webhooks/razorpay`;
                            navigator.clipboard.writeText(url);
                            toast.success('Webhook URL copied');
                          }}
                          data-testid="razorpay-webhook-url-copy-btn"
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copy
                        </Button>
                      </div>
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center">3</span>
                    <span>
                      Under{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Active Events</span>,
                      tick{' '}
                      <span className="font-mono bg-white dark:bg-slate-900 px-1 py-0.5 rounded text-[11px] border border-indigo-200 dark:border-indigo-800">payment.captured</span>
                      {' '}then click{' '}
                      <span className="font-semibold text-slate-800 dark:text-slate-100">Create Webhook</span>.
                      That&apos;s it — Out-Play handles the rest.
                    </span>
                  </li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations & Comms Tab */}
        <TabsContent value="integrations">
          <IntegrationsTab />
        </TabsContent>

        {/* Phase 89E — Outbound Webhooks Tab */}
        <TabsContent value="webhooks">
          <WebhooksSettings />
        </TabsContent>

        {/* Phase 300 — Automation Flags */}
        <TabsContent value="automation">
          <AutomationFlagsPanel />
        </TabsContent>

        {/* StreamX Sprint 3a — Privacy & Session Replay tab */}
        <TabsContent value="privacy">
          <SessionReplayCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// Phase 85 — Wall of Champions admin panel
function WallOfChampionsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/academy/wall-of-champions/settings').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateSetting = async (patch) => {
    setSaving(true);
    try {
      const r = await api.post('/academy/wall-of-champions/settings', patch);
      setData(r.data);
      toast.success('Wall settings updated');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-10 text-center text-sm text-muted-foreground">Loading...</div>;
  if (!data) return <div className="py-10 text-center text-sm text-muted-foreground">Could not load settings</div>;

  const fullUrl = `${window.location.origin}${data.public_path}`;
  const enabled = data.settings.enabled;

  const copyLink = () => {
    const text = fullUrl;
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast.success('Link copied'); } catch {}
      document.body.removeChild(ta);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => toast.success('Link copied')).catch(fallback);
    } else fallback();
  };
  const shareWa = () => {
    const text = `🏆 Check out our Wall of Champions! ${fullUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="wall-settings-tab">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-600" /> Wall of Champions</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">Publish a beautiful, public Top-N roster — every parent shares it, every coach feels celebrated, and it doubles as a lead funnel into your CRM.</p>

          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border">
            <div>
              <p className="text-sm font-semibold flex items-center gap-1.5">
                {enabled ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-muted-foreground" />}
                {enabled ? 'Wall is live' : 'Wall is hidden'}
              </p>
              <p className="text-[11px] text-muted-foreground">When enabled, anyone with the link sees your Top {data.settings.top_n} performers.</p>
            </div>
            <Button size="sm" onClick={() => updateSetting({ enabled: !enabled })} disabled={saving} variant={enabled ? 'outline' : 'default'}
              className={!enabled ? 'bg-emerald-600 hover:bg-emerald-700' : ''} data-testid="wall-toggle-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              {enabled ? 'Disable wall' : 'Enable wall'}
            </Button>
          </div>

          {enabled && (
            <>
              <div>
                <Label className="text-xs">Public URL</Label>
                <div className="flex gap-1.5 mt-1.5">
                  <Input value={fullUrl} readOnly className="h-9 text-xs font-mono" data-testid="wall-public-url" />
                  <Button size="sm" variant="outline" onClick={copyLink} className="h-9" data-testid="wall-copy-url"><Copy className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={shareWa} className="h-9" data-testid="wall-share-wa-btn"><Share2 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(fullUrl, '_blank')} className="h-9" data-testid="wall-open-btn"><ExternalLink className="h-3.5 w-3.5" /></Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Show top</Label>
                  <Input type="number" min="3" max="30" value={data.settings.top_n}
                    onChange={(e) => setData({ ...data, settings: { ...data.settings, top_n: parseInt(e.target.value, 10) } })}
                    onBlur={(e) => updateSetting({ top_n: parseInt(e.target.value, 10) })}
                    className="h-9" data-testid="wall-top-n" />
                </div>
                <div>
                  <Label className="text-xs">Show batch</Label>
                  <select value={data.settings.show_batch ? 'yes' : 'no'} onChange={(e) => updateSetting({ show_batch: e.target.value === 'yes' })}
                    className="h-9 w-full border rounded-md bg-background px-2 text-sm" data-testid="wall-show-batch">
                    <option value="yes">Yes</option><option value="no">No</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border bg-emerald-50/50 dark:bg-emerald-950/20">
                <div>
                  <p className="text-sm font-semibold">Allow public enquiries</p>
                  <p className="text-[11px] text-muted-foreground">Parents can submit a contact form right from the wall — leads land in your CRM.</p>
                </div>
                <Button size="sm" variant={data.settings.allow_enquiries ? 'default' : 'outline'}
                  className={data.settings.allow_enquiries ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  onClick={() => updateSetting({ allow_enquiries: !data.settings.allow_enquiries })} disabled={saving}
                  data-testid="wall-toggle-enquiries">
                  {data.settings.allow_enquiries ? 'Enabled' : 'Disabled'}
                </Button>
              </div>

              {/* Phase 264 — Hero team photo (16:9 widescreen) shown above the roster on the public wall. */}
              <div className="space-y-2 pt-1">
                <Label className="text-xs">Team / squad photo URL <span className="text-muted-foreground font-normal">(optional, 16:9 recommended)</span></Label>
                <div className="flex gap-1.5">
                  <Input
                    placeholder="https://… or /uploads/your-team.jpg"
                    value={data.settings.hero_image_url || ''}
                    onChange={(e) => setData({ ...data, settings: { ...data.settings, hero_image_url: e.target.value } })}
                    onBlur={(e) => updateSetting({ hero_image_url: e.target.value.trim() })}
                    className="h-9 text-xs font-mono"
                    data-testid="wall-hero-image-url"
                  />
                  {data.settings.hero_image_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9"
                      onClick={() => { setData({ ...data, settings: { ...data.settings, hero_image_url: '' } }); updateSetting({ hero_image_url: '' }); }}
                      data-testid="wall-hero-image-clear"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {data.settings.hero_image_url && (
                  <div className="rounded-lg border bg-muted/30 overflow-hidden" data-testid="wall-hero-image-preview">
                    <div className="aspect-video w-full">
                      <img
                        src={data.settings.hero_image_url}
                        alt="Team hero preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                    <p className="text-[10px] px-3 py-1.5 text-muted-foreground">Preview · 16:9 widescreen</p>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Tip: A wide team huddle, championship photo, or training shot works best. The image is auto-cropped to 16:9 and overlaid with a subtle gradient for readability.
                </p>
              </div>

              {/* Phase 264 — Embeddable Top Moments widget for academy websites */}
              <EmbedTopMomentsBlock slug={data.slug} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



// (EmbedTopMomentsBlock moved to /app/frontend/src/components/ in Phase 266f
//  for reuse from the dashboard Embed Performance tile dialog.)
